import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import NotificationItem from './NotificationItem';
import { NOTIFICATION_SECTION_LABELS } from './notificationHelpers';
import {
  NotificationSkeletonList,
  NotificationSkeletonLoadMore,
} from './NotificationSkeleton';
import useNotificationInbox from '../../../hooks/useNotificationInbox';

/**
 * Shared notification list body (sections, loading, mark-all, see-more).
 * Used by NotificationPopup and NotificationOverlay.
 */
const NotificationInboxContent = ({
  open,
  onNavigateAway,
  headerExtra = null,
  avatarSize = 24,
  contentPaddingTop = 0,
}) => {
  const {
    listLoading,
    listLoadingMore,
    listError,
    unreadCount,
    hasMorePages,
    hasAny,
    sectionBlocks,
    mapToItemProps,
    handleSeeMore,
    handleNotificationClick,
    handleMarkAllRead,
  } = useNotificationInbox({ open, onNavigateAway });

  const renderSection = (
    sectionKey,
    rawItems,
    headingOverride,
    isFirstVisibleSection
  ) => {
    if (!rawItems || rawItems.length === 0) return null;

    const heading =
      headingOverride ??
      NOTIFICATION_SECTION_LABELS[sectionKey] ??
      sectionKey;

    return (
      <Box key={sectionKey} sx={{ padding: '0 16px' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: isFirstVisibleSection
              ? '16px 0 12px 0'
              : '21px 0 12px 0',
          }}
        >
          <Typography
            sx={{
              color: '#405261',
              fontFamily: 'Inter',
              fontSize: '10px',
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: '12px',
              textTransform: 'none',
              whiteSpace: 'nowrap',
              letterSpacing: '0.02em',
            }}
          >
            {heading}
          </Typography>

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              height: '1px',
              backgroundColor: '#F1F3F5',
            }}
          />
        </Box>

        <Box>
          {rawItems.map((item) => (
            <NotificationItem
              key={item._id}
              notification={mapToItemProps(item)}
              onClick={() => handleNotificationClick(item)}
              avatarSize={avatarSize}
            />
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        paddingTop: contentPaddingTop,
      }}
    >
      {headerExtra}

      {listLoading && !listLoadingMore ? (
        <NotificationSkeletonList rows={7} />
      ) : (
        <>
          {unreadCount > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                padding: '16px 16px 0 16px',
              }}
            >
              <Button
                size="small"
                onClick={handleMarkAllRead}
                sx={{
                  textTransform: 'none',
                  fontSize: '12px',
                  fontFamily: 'Inter',
                  color: '#148DFD',
                  minWidth: 0,
                  p: 0,
                }}
              >
                Mark all as read
              </Button>
            </Box>
          )}

          {listError && (
            <Typography sx={{ px: '16px', py: 2, color: '#FF4D4F', fontSize: 13 }}>
              {typeof listError === 'string'
                ? listError
                : listError?.message || 'Failed to load notifications'}
            </Typography>
          )}

          {!hasAny && !listError && (
            <Typography
              sx={{
                px: '16px',
                py: 4,
                color: '#405261',
                fontFamily: 'Inter',
                fontSize: '14px',
                textAlign: 'center',
              }}
            >
              No notifications yet
            </Typography>
          )}

          {sectionBlocks.map((block, index) =>
            renderSection(block.key, block.items, block.label, index === 0)
          )}

          {listLoadingMore && <NotificationSkeletonLoadMore rows={2} />}

          {!listError && hasAny && hasMorePages && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                padding: '8px 16px 16px',
              }}
            >
              <Button
                size="small"
                onClick={handleSeeMore}
                disabled={listLoadingMore}
                sx={{
                  textTransform: 'none',
                  fontSize: '13px',
                  fontFamily: 'Inter',
                  fontWeight: 500,
                  color: '#148DFD',
                }}
              >
                {listLoadingMore ? 'Loading…' : 'See more'}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default NotificationInboxContent;
